/**
 * HTML text helpers for hosts that build markup as strings.
 */

const ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const UNSAFE = /[&<>"']/g;

/**
 * Escapes the five characters that break out of HTML text or an attribute
 * value, in either quote style. `null` / `undefined` become `''`.
 *
 * Escape each raw value once — an already-escaped string escapes again.
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(UNSAFE, (char) => ENTITIES[char] as string);
}

/**
 * Inline tokens editors can put inside a copy field to style part of a line.
 * `sp` carries a class because a bare `<span>` gives CSS nothing to select.
 */
const INLINE_TAGS: Record<string, string> = {
  i: '<em>|</em>',
  b: '<strong>|</strong>',
  sp: '<span class="kz-sp">|</span>',
};

const TOKEN = /\[(i|b|sp)\]([\s\S]*?)\[\/\1\]/g;

/**
 * Escapes `value`, then turns the inline tokens into tags:
 * `[i]` → `<em>`, `[b]` → `<strong>`, `[sp]` → `<span class="kz-sp">`.
 *
 * Everything else stays escaped, so the result is safe to insert as HTML. An
 * unclosed token is left as literal text.
 */
export function formatText(value: unknown): string {
  return escapeHtml(value).replace(TOKEN, (_match, tag: string, inner: string) => {
    const [open, close] = (INLINE_TAGS[tag] as string).split('|');
    return `${open}${inner}${close}`;
  });
}
